import { Layout } from "@/components/layout/Layout";
import { useLanguage } from "@/lib/i18n";

const Privacy = () => {
  const { t } = useLanguage();

  const getTranslation = (key: string): string => t(`privacy.${key}`);

  const getItems = (key: string): string[] => {
    const value = t(`privacy.${key}`);
    if (typeof value === 'string' && value.startsWith('privacy.')) {
      return [];
    }
    return value as unknown as string[];
  };

  return (
    <Layout>
      <div className="pt-28 pb-16 sm:pt-32 sm:pb-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {getTranslation("title")}
            </h1>
            <p className="text-muted-foreground mb-12">
              {getTranslation("lastUpdated")}
            </p>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
              {/* 1. Introduction */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("intro.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {getTranslation("intro.content")}
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  {getTranslation("intro.consent")}
                </p>
              </section>

              {/* 2. Information We Collect */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("infoWeCollect.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {getTranslation("infoWeCollect.intro")}
                </p>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium text-foreground mb-2">
                      {getTranslation("infoWeCollect.directInfo.title")}
                    </h3>
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                      {getItems("infoWeCollect.directInfo.items").map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-foreground mb-2">
                      {getTranslation("infoWeCollect.autoInfo.title")}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {getTranslation("infoWeCollect.autoInfo.content")}
                    </p>
                  </div>
                </div>
              </section>

              {/* 3. Usage Data */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("usageData.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {getTranslation("usageData.content")}
                </p>
              </section>

              {/* 4. How We Use Your Information */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("howWeUse.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  {getTranslation("howWeUse.intro")}
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  {getItems("howWeUse.items").map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4 font-medium">
                  {getTranslation("howWeUse.noSell")}
                </p>
              </section>

              {/* 5. Where and How We Store Your Data */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("howWeStore.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  {getTranslation("howWeStore.intro")}
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  {getItems("howWeStore.items").map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              {/* 6. Sharing with Third Parties */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("thirdParty.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  {getTranslation("thirdParty.intro")}
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  {getItems("thirdParty.items").map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              {/* 7. Data Security */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("dataSecurity.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {getTranslation("dataSecurity.content")}
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  {getTranslation("dataSecurity.disclaimer")}
                </p>
              </section>

              {/* 8. Data Retention and Deletion */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("dataRetention.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  {getTranslation("dataRetention.intro")}
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  {getItems("dataRetention.items").map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              {/* 9. Your Rights */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("yourRights.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  {getTranslation("yourRights.intro")}
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  {getItems("yourRights.items").map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              {/* 10. Children's Privacy */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("childrenPrivacy.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {getTranslation("childrenPrivacy.content")}
                </p>
              </section>

              {/* 11. Changes to This Policy */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("policyChanges.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {getTranslation("policyChanges.content")}
                </p>
              </section>

              {/* 12. Contact Us */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  {getTranslation("contactUs.title")}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {getTranslation("contactUs.content")}{" "}
                  <a href={`mailto:${getTranslation("contactUs.email")}`} className="text-primary hover:underline">
                    {getTranslation("contactUs.email")}
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
